import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { teamsApi, teamMembersApi, eventsApi, gamesApi, usersApi, getStaticUrl } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import {
  ArrowLeftIcon, EditIcon, PlusIcon, TrashIcon,
  UsersIcon, CalendarIcon, TrophyIcon, CloseIcon, UserMinusIcon
} from '../Icons/Icons';
import './TeamDetail.css';

const GAME_ACCENTS = {
  'Valorant':          { color: '#ff4655', glow: 'rgba(255,70,85,0.25)',  badge: 'rgba(255,70,85,0.15)',  border: 'rgba(255,70,85,0.3)' },
  'League of Legends': { color: '#c89b3c', glow: 'rgba(200,155,60,0.25)', badge: 'rgba(200,155,60,0.15)', border: 'rgba(200,155,60,0.3)' },
  'CS2':               { color: '#53ffab', glow: 'rgba(83,255,171,0.2)',  badge: 'rgba(83,255,171,0.12)', border: 'rgba(83,255,171,0.3)' },
  'Counter-Strike 2':  { color: '#53ffab', glow: 'rgba(83,255,171,0.2)',  badge: 'rgba(83,255,171,0.12)', border: 'rgba(83,255,171,0.3)' },
  'Rocket League':     { color: '#468dff', glow: 'rgba(70,141,255,0.25)', badge: 'rgba(70,141,255,0.15)', border: 'rgba(70,141,255,0.3)' },
};
const DEFAULT_ACCENT = { color: '#d2bcff', glow: 'rgba(135,70,255,0.25)', badge: 'rgba(135,70,255,0.15)', border: 'rgba(210,188,255,0.3)' };
const getAccent = (gameName) => GAME_ACCENTS[gameName] || DEFAULT_ACCENT;

const STAFF_ROLES = new Set(['coach', 'manager', 'analyst', 'responsable', 'directeur', 'staff']);
const isStaffRole = (role) => role && STAFF_ROLES.has(role.toLowerCase());

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

const avatarSeed = (name = '') => {
  const palette = ['#ff4655','#c89b3c','#53ffab','#468dff','#d2bcff','#ff9f43','#54a0ff','#8746ff'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return palette[Math.abs(h) % palette.length];
};

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const EVENT_LABELS = { scrim: 'Scrim', tournament: 'Tournoi', training: 'Entraînement', meeting: 'Réunion' };
const ROLE_LABELS  = { player: 'Joueur', coach: 'Coach', manager: 'Manager', analyst: 'Analyste', substitute: 'Remplaçant' };

// Système de rôle → rôles équipe compatibles
const SYSTEM_ROLE_FOR_TEAM_ROLE = {
  player:     ['player'],
  substitute: ['player'],
  coach:      ['coach'],
  manager:    ['manager', 'responsable', 'ceo', 'coo'],
  analyst:    ['player', 'responsable'],
};

const TeamDetail = () => {
  const { teamId } = useParams();
  const navigate   = useNavigate();
  const { hasPermission } = useAuth();

  const canEdit     = hasPermission('teams.edit');
  const canManage   = hasPermission('teams.manage_members');
  const canDelete   = hasPermission('teams.delete');
  const canEditLogo = hasPermission('teams.edit_logo');

  const [team,     setTeam]     = useState(null);
  const [members,  setMembers]  = useState([]);
  const [events,   setEvents]   = useState([]);
  const [games,    setGames]    = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [stats,    setStats]    = useState({ wins: 0, losses: 0, draws: 0, total: 0 });

  const [showEditModal,  setShowEditModal]  = useState(false);
  const [editForm,       setEditForm]       = useState({ name: '', game_id: '', coach: '', description: '' });
  const [showAddMember,  setShowAddMember]  = useState(false);
  const [addMemberSearch, setAddMemberSearch] = useState('');
  const [addMemberRole,  setAddMemberRole]  = useState('player');
  const [addMemberUserId, setAddMemberUserId] = useState(null);
  const [editingMmrId,   setEditingMmrId]   = useState(null);
  const [mmrValue,       setMmrValue]       = useState('');
  const [uploadingLogo,  setUploadingLogo]  = useState(false);

  useEffect(() => { loadData(); }, [teamId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [teamRes, membersRes, eventsRes, gamesRes, usersRes] = await Promise.all([
        teamsApi.getById(teamId),
        teamMembersApi.getTeamMembers(teamId),
        eventsApi.getAll(),
        gamesApi.getAll(),
        usersApi.getAll(),
      ]);
      const t = teamRes.data;
      setTeam(t);
      setGames(gamesRes.data || []);
      setAllUsers(usersRes.data || []);
      const membersList = membersRes.data || [];
      setMembers(membersList);

      const getDate  = (ev) => ev.start_datetime || ev.start_time || ev.date;
      const now      = new Date();
      const teamEvs  = (eventsRes.data || []).filter(ev => {
        const ids = ev.team_ids?.length ? ev.team_ids : (ev.team_id ? [ev.team_id] : []);
        return ids.includes(parseInt(teamId));
      });
      setEvents(teamEvs.filter(ev => new Date(getDate(ev)) >= now)
        .sort((a,b) => new Date(getDate(a)) - new Date(getDate(b))).slice(0, 5));

      const scored = teamEvs.filter(ev => ev.score && (ev.type === 'scrim' || ev.type === 'tournament'));
      let wins = 0, losses = 0, draws = 0;
      scored.forEach(ev => {
        const p = ev.score.split('-').map(s => parseInt(s.trim()));
        if (p.length === 2) { if (p[0] > p[1]) wins++; else if (p[0] < p[1]) losses++; else draws++; }
      });
      setStats({ wins, losses, draws, total: scored.length });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getGameName = (id) => games.find(g => g.id === id)?.name || '';
  const getUserName = (uid) => {
    const u = allUsers.find(u => u.id === uid);
    return u ? (u.username || u.real_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || `#${uid}`) : `#${uid}`;
  };

  const handleSaveEdit = async () => {
    try {
      await teamsApi.update(teamId, { name: editForm.name, game_id: editForm.game_id || null, coach: editForm.coach || null, description: editForm.description || null });
      setShowEditModal(false); loadData();
    } catch { alert('Erreur lors de la sauvegarde'); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      alert('Format non supporté. Utilisez JPG, PNG, GIF ou WebP.');
      return;
    }
    if (file.size > 5_000_000) {
      alert('Image trop volumineuse (5 Mo maximum).');
      return;
    }
    setUploadingLogo(true);
    try {
      const res = await teamsApi.uploadLogo(teamId, file);
      setTeam(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de l\'upload de l\'image');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Retirer l\'image de l\'équipe ?')) return;
    try {
      const res = await teamsApi.deleteLogo(teamId);
      setTeam(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la suppression de l\'image');
    }
  };

  const handleRemoveMember = async (uid) => {
    if (!window.confirm('Retirer ce membre ?')) return;
    try { await teamMembersApi.removeMember(teamId, uid); loadData(); } catch (err) { console.error(err); }
  };

  const handleAddMember = async () => {
    if (!addMemberUserId) return;
    try {
      await teamMembersApi.addMember(teamId, addMemberUserId, addMemberRole);
      setShowAddMember(false); setAddMemberSearch(''); setAddMemberUserId(null); loadData();
    } catch { alert('Erreur lors de l\'ajout'); }
  };

  const handleSaveMmr = async (uid) => {
    try { await teamMembersApi.updateMemberMMR(teamId, uid, parseInt(mmrValue), true); setEditingMmrId(null); loadData(); }
    catch (err) { console.error(err); }
  };

  const handleDeleteTeam = async () => {
    if (!window.confirm(`Supprimer "${team?.name}" définitivement ?`)) return;
    try { await teamsApi.delete(teamId); navigate('/equipes'); } catch { alert('Erreur lors de la suppression'); }
  };

  const currentMemberIds = new Set(members.map(m => m.user_id));
  const allowedSystemRoles = SYSTEM_ROLE_FOR_TEAM_ROLE[addMemberRole] || ['player'];
  const availableToAdd = allUsers.filter(u =>
    !currentMemberIds.has(u.id) &&
    allowedSystemRoles.includes(u.role) &&
    (!addMemberSearch || getUserName(u.id).toLowerCase().includes(addMemberSearch.toLowerCase()))
  );

  const players = members.filter(m => !isStaffRole(m.team_role));
  const staff   = members.filter(m =>  isStaffRole(m.team_role));

  if (loading) return (
    <div className="td-loading"><div className="td-spinner" /></div>
  );

  if (!team) return (
    <div className="td-loading">
      <p style={{ color: 'var(--text-secondary)' }}>Équipe introuvable.</p>
      <button className="btn-secondary" onClick={() => navigate('/equipes')}>Retour</button>
    </div>
  );

  const gameName = getGameName(team.game_id);
  const accent   = getAccent(gameName);
  const winRate  = stats.total > 0 ? Math.round((stats.wins / stats.total) * 100) : null;
  const visibleAvatars = members.slice(0, 5);

  return (
    <div className="td-root">
      {/* ── AMBIENT BLOBS ── */}
      <div className="td-blob td-blob-1" style={{ background: `radial-gradient(circle, ${accent.glow} 0%, transparent 70%)` }} />
      <div className="td-blob td-blob-2" />

      {/* ── BACK ── */}
      <button className="td-back" onClick={() => navigate('/equipes')}>
        <ArrowLeftIcon size={16} /> Équipes
      </button>

      {/* ── HERO CARD ── */}
      <div className="td-hero glass-td" style={{ '--a-color': accent.color, '--a-glow': accent.glow, '--a-badge': accent.badge, '--a-border': accent.border }}>
        <div className="td-hero-glow" style={{ background: `radial-gradient(ellipse at 60% 0%, ${accent.glow} 0%, transparent 70%)` }} />

        <div className="td-hero-top">
          {/* Game + Status badge */}
          <div className="td-hero-badges">
            {gameName && (
              <span className="td-game-badge" style={{ color: accent.color, background: accent.badge, border: `1px solid ${accent.border}` }}>
                {gameName}
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="td-hero-actions">
            {canEdit && (
              <button className="td-btn-glass" onClick={() => { setEditForm({ name: team.name || '', game_id: team.game_id || '', coach: team.coach || '', description: team.description || '' }); setShowEditModal(true); }}>
                <EditIcon size={14} /> Modifier
              </button>
            )}
            {canManage && (
              <button className="td-btn-primary" onClick={() => setShowAddMember(true)}>
                <PlusIcon size={14} /> Ajouter
              </button>
            )}
            {canDelete && (
              <button className="td-btn-danger" onClick={handleDeleteTeam}>
                <TrashIcon size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Logo + name */}
        <div className="td-identity">
          <div className="td-team-logo" style={{ borderColor: accent.border, background: accent.badge, color: accent.color }}>
            {team.logo
              ? <img src={getStaticUrl(team.logo)} alt={team.name} />
              : <span>{getInitials(team.name)}</span>}

            {canEditLogo && (
              <>
                <label className="td-logo-edit" title="Changer l'image de l'équipe">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    hidden
                    disabled={uploadingLogo}
                    onChange={handleLogoUpload}
                  />
                  {uploadingLogo ? '…' : <EditIcon size={16} />}
                </label>
                {team.logo && !uploadingLogo && (
                  <button className="td-logo-remove" title="Retirer l'image" onClick={handleLogoDelete}>
                    <CloseIcon size={12} />
                  </button>
                )}
              </>
            )}
          </div>
          <h1 className="td-team-name">{team.name}</h1>
        </div>
        <div className="td-hero-meta">
          <span><UsersIcon size={14} /> {members.length} membres</span>
          {team.coach && <span>· Coach : {team.coach}</span>}
          {team.average_mmr && <span>· MMR <strong style={{ color: accent.color }}>{team.average_mmr}</strong></span>}
        </div>
        {team.description && <p className="td-hero-desc">{team.description}</p>}

        {/* Avatar stack */}
        {members.length > 0 && (
          <div className="td-avatar-stack">
            {visibleAvatars.map((m) => {
              const name = getUserName(m.user_id);
              return (
                <div key={m.user_id} className="td-avatar-circle" style={{ background: avatarSeed(name) }} title={name}>
                  {getInitials(name)}
                </div>
              );
            })}
            {members.length > 5 && (
              <div className="td-avatar-circle td-avatar-more">+{members.length - 5}</div>
            )}
          </div>
        )}
      </div>

      {/* ── CONTENT GRID ── */}
      <div className="td-grid">

        {/* ── ROSTER ── */}
        <section className="td-card glass-td">
          <div className="td-card-hd">
            <div className="td-card-hd-left">
              <h2><UsersIcon size={15} /> Roster</h2>
              <span className="td-pill-count">{players.length}</span>
            </div>
          </div>

          {players.length === 0 ? (
            <p className="td-empty">Aucun joueur dans le roster.</p>
          ) : (
            <ul className="td-member-list">
              {players.map(m => {
                const name = getUserName(m.user_id);
                return (
                  <li key={m.user_id} className="td-member-row">
                    <div className="td-av" style={{ background: avatarSeed(name) }}>
                      {getInitials(name)}
                    </div>
                    <div className="td-member-info">
                      <span className="td-member-name">{name}</span>
                      {m.team_role && (
                        <span className="td-role-pill" style={{ color: accent.color, background: accent.badge }}>
                          {ROLE_LABELS[m.team_role] || m.team_role}
                        </span>
                      )}
                    </div>
                    <div className="td-member-right">
                      {editingMmrId === m.user_id ? (
                        <div className="td-mmr-edit">
                          <input type="number" className="td-mmr-input" value={mmrValue}
                            onChange={e => setMmrValue(e.target.value)} placeholder="MMR" autoFocus />
                          <button className="td-icon-btn" onClick={() => handleSaveMmr(m.user_id)}>✓</button>
                          <button className="td-icon-btn" onClick={() => setEditingMmrId(null)}>✕</button>
                        </div>
                      ) : (
                        <>
                          {m.mmr && <span className="td-mmr-badge">{m.mmr}</span>}
                          {canManage && (
                            <button className="td-icon-btn" title="Modifier MMR"
                              onClick={() => { setEditingMmrId(m.user_id); setMmrValue(m.mmr || ''); }}>
                              <EditIcon size={12} />
                            </button>
                          )}
                        </>
                      )}
                      {canManage && (
                        <button className="td-icon-btn td-icon-danger" title="Retirer"
                          onClick={() => handleRemoveMember(m.user_id)}>
                          <UserMinusIcon size={14} />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* ── SIDEBAR ── */}
        <div className="td-side">

          {/* Staff */}
          {(staff.length > 0 || canManage) && (
            <section className="td-card glass-td">
              <div className="td-card-hd">
                <div className="td-card-hd-left">
                  <h2>Staff</h2>
                  <span className="td-pill-count">{staff.length}</span>
                </div>
              </div>
              {staff.length === 0 ? (
                <p className="td-empty">Aucun staff renseigné.</p>
              ) : (
                <ul className="td-member-list">
                  {staff.map(m => {
                    const name = getUserName(m.user_id);
                    return (
                      <li key={m.user_id} className="td-member-row">
                        <div className="td-av" style={{ background: avatarSeed(name) }}>{getInitials(name)}</div>
                        <div className="td-member-info">
                          <span className="td-member-name">{name}</span>
                          <span className="td-role-pill" style={{ color: 'var(--text-secondary)', background: 'var(--bg-tertiary)' }}>
                            {ROLE_LABELS[m.team_role?.toLowerCase()] || m.team_role}
                          </span>
                        </div>
                        {canManage && (
                          <button className="td-icon-btn td-icon-danger" onClick={() => handleRemoveMember(m.user_id)}>
                            <UserMinusIcon size={14} />
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}

          {/* Stats */}
          <section className="td-card glass-td">
            <div className="td-card-hd">
              <div className="td-card-hd-left"><h2><TrophyIcon size={15} /> Statistiques</h2></div>
              <button className="td-link-btn" onClick={() => navigate(`/equipes/${teamId}/statistiques`)}>
                Tout voir →
              </button>
            </div>
            {stats.total === 0 ? (
              <p className="td-empty">Aucun match enregistré.</p>
            ) : (
              <div className="td-stats-row">
                <div className="td-stat">
                  <span className="td-stat-val" style={{ color: 'var(--success)' }}>{stats.wins}</span>
                  <span className="td-stat-lbl">V</span>
                </div>
                <div className="td-stat">
                  <span className="td-stat-val" style={{ color: 'var(--error)' }}>{stats.losses}</span>
                  <span className="td-stat-lbl">D</span>
                </div>
                {stats.draws > 0 && (
                  <div className="td-stat">
                    <span className="td-stat-val">{stats.draws}</span>
                    <span className="td-stat-lbl">N</span>
                  </div>
                )}
                {winRate !== null && (
                  <div className="td-stat">
                    <span className="td-stat-val" style={{ color: accent.color }}>{winRate}%</span>
                    <span className="td-stat-lbl">Win rate</span>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Events */}
          <section className="td-card glass-td">
            <div className="td-card-hd">
              <div className="td-card-hd-left"><h2><CalendarIcon size={15} /> Événements à venir</h2></div>
            </div>
            {events.length === 0 ? (
              <p className="td-empty">Aucun événement prévu.</p>
            ) : (
              <ul className="td-event-list">
                {events.map(ev => (
                  <li key={ev.id} className="td-event-row" onClick={() => navigate(`/events/${ev.id}`)}>
                    <span className="td-event-type-pill">{EVENT_LABELS[ev.type] || ev.type}</span>
                    <div className="td-event-info">
                      <span className="td-event-name">{ev.title || ev.name}</span>
                      <span className="td-event-date">{formatDate(ev.start_datetime || ev.start_time || ev.date)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      {/* ── EDIT MODAL ── */}
      {showEditModal && (
        <div className="td-overlay" onClick={() => setShowEditModal(false)}>
          <div className="td-modal glass-td" onClick={e => e.stopPropagation()}>
            <div className="td-modal-hd">
              <h3>Modifier l'équipe</h3>
              <button className="td-icon-btn" onClick={() => setShowEditModal(false)}><CloseIcon size={18} /></button>
            </div>
            <div className="td-modal-body">
              <label>Nom<input className="td-input" value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))} /></label>
              <label>Jeu
                <select className="td-input" value={editForm.game_id} onChange={e => setEditForm(f => ({...f, game_id: e.target.value}))}>
                  <option value="">— Aucun —</option>
                  {games.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </label>
              <label>Description<textarea className="td-input td-textarea" value={editForm.description} onChange={e => setEditForm(f => ({...f, description: e.target.value}))} rows={3} /></label>
            </div>
            <div className="td-modal-ft">
              <button className="btn-cancel" onClick={() => setShowEditModal(false)}>Annuler</button>
              <button className="btn-primary" onClick={handleSaveEdit}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD MEMBER MODAL ── */}
      {showAddMember && (
        <div className="td-overlay" onClick={() => setShowAddMember(false)}>
          <div className="td-modal glass-td" onClick={e => e.stopPropagation()}>
            <div className="td-modal-hd">
              <h3>Ajouter un membre</h3>
              <button className="td-icon-btn" onClick={() => setShowAddMember(false)}><CloseIcon size={18} /></button>
            </div>
            <div className="td-modal-body">
              <label>Rôle
                <select className="td-input" value={addMemberRole} onChange={e => { setAddMemberRole(e.target.value); setAddMemberSearch(''); setAddMemberUserId(null); }}>
                  {Object.entries(ROLE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </label>
              <label>Joueur
                <input className="td-input" placeholder="Rechercher…" value={addMemberSearch}
                  onChange={e => { setAddMemberSearch(e.target.value); setAddMemberUserId(null); }} />
              </label>
              <ul className="td-user-list">
                {availableToAdd.slice(0, 8).map(u => {
                  const name = getUserName(u.id);
                  return (
                    <li key={u.id} className={`td-user-item ${addMemberUserId === u.id ? 'selected' : ''}`}
                      onClick={() => setAddMemberUserId(u.id)}>
                      <div className="td-av td-av-sm" style={{ background: avatarSeed(name) }}>{getInitials(name)}</div>
                      <span>{name}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="td-modal-ft">
              <button className="btn-cancel" onClick={() => setShowAddMember(false)}>Annuler</button>
              <button className="btn-primary" disabled={!addMemberUserId} onClick={handleAddMember}>Ajouter</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDetail;
